/* eslint-disable no-console */
import _ from 'lodash';

import { getSelectorValue } from './elements';

// Example :
// fetchUser = async (userID) => {
//   return enhancedQuery(client, USER.READ(), {
//     options: {
//       variables: { aws_username: userID },
//       onError: (err) => {
//            setOpenSnackbar({
//              variant: 'error',
//              message: err.msg,
//            });
//          },
//     },
//     defaultData: {
//       readUser: {
//         affiliate_id: null,
//         aws_username: null,
//         company_id: null,
//         created_at: null,
//         email: null,
//         id: null,
//         mobile: null,
//         mobile_prefix: null,
//         name: null,
//         preferred_name: null,
//         role: null,
//       },
//     },
//     selector: (d) => d.readUser,
//   });
// }

export const enhancedQuery = async (client, query, props = {
  options: {},
  defaultData: {},
  selector: () => {},
}) => {
  let resp;
  try {
    resp = await client.query({ query, ...props.options });
  } catch (err) {
    // error can be handled outside. ex: snackbar
    if (props.options && props.options.onError) {
      props.options.onError(err);
    }
    // default data fallback when error occurred
    return getSelectorValue(props.defaultData, props.selector);
  }
  return getSelectorValue({
    ...props.defaultData,
    ...resp.data,
  }, props.selector);
};

export const clientContextWarn = (err) => {
  if (
    err
    && _.has(err, 'graphQLErrors')
    && _.isArray(err.graphQLErrors)
    && err.graphQLErrors.some((e) => e.message.includes('UnknownType') || e.message.includes('FieldUndefined'))
  ) {
    console.warn('Are you sure you are using the correct client context? Did you forgot to export your component with `withClientContext(clientName)` ?');
  }
};

// Apollo pass __typename in result so need to stripe out else mutation wont work properly
export const stripTypename = (value) => {
  if (value === null || value === undefined) {
    return value;
  }
  if (Array.isArray(value)) {
    return value.map((v) => stripTypename(v));
  }
  if (typeof value === 'object') {
    const newObj = {};
    Object.entries(value).forEach(([key, v]) => {
      if (key !== '__typename') {
        newObj[key] = stripTypename(v);
      }
    });
    return newObj;
  }
  return value;
};

// Apollo Client 3 dropped the "GraphQL error: " prefix that Apollo 2 put on ApolloError.message,
// so every `err.message === 'GraphQL error: forbidden'` check went dead when LMS-4538 upgraded the
// client: a resolver refusing with "forbidden" or "item not found" fell through to the generic
// "An error has occured" snackbar and a detail page rendering with no data (LMS-5154). Read the
// resolver messages off graphQLErrors instead, tolerating the legacy prefix wherever it survives.
const LEGACY_GRAPHQL_PREFIX = /^GraphQL error:\s*/;

export const graphQLErrorMessages = (err) => {
  if (!err) {
    return [];
  }
  if (Array.isArray(err.graphQLErrors) && err.graphQLErrors.length > 0) {
    return err.graphQLErrors.map((e) => String(e?.message || '').replace(LEGACY_GRAPHQL_PREFIX, ''));
  }
  return err.message ? [String(err.message).replace(LEGACY_GRAPHQL_PREFIX, '')] : [];
};

export const hasGraphQLError = (err, message) => graphQLErrorMessages(err).includes(message);

export const isUnauthorizedNetworkError = (err) => Boolean(
  err
  && (
    err.networkError?.statusCode === 401
    || /Received status code 401/.test(String(err.message || ''))
  ),
);
